import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Send, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DigestTemplateEditor from '../components/DigestTemplateEditor';

const AdminEmail = () => {
  const { token } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        fetch(`${API_BASE}/admin/email/stats`, { headers: headers() }),
        // 200 is the API max. At 50 the tail of the list was simply invisible.
        fetch(`${API_BASE}/admin/email/recent?limit=200`, { headers: headers() }),
      ]);
      if (s.ok) setStats(await s.json());
      if (r.ok) setRecent(await r.json());
    } catch (e) {
      setMessage('Failed to load email data.');
    }
    setLoading(false);
  }, [API_BASE, headers]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const sendNow = async () => {
    if (!window.confirm('Send the release digest now to all confirmed subscribers?')) return;
    setSending(true);
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/admin/email/send-digest`, {
        method: 'POST',
        headers: headers(),
      });
      setMessage(resp.ok ? 'Digest send started in the background.' : 'Failed to start the send.');
    } catch (e) {
      setMessage('Failed to start the send.');
    }
    setSending(false);
  };

  // Row actions. Suppress is reversible and keeps the record; delete is not,
  // and is meant for rows that should never have existed (bot/scraped
  // enrolments) rather than for anyone who may have genuinely opted in.
  const [busyId, setBusyId] = useState(null);

  const rowAction = async (row, action) => {
    if (action === 'delete' &&
        !window.confirm(`Permanently delete ${row.email}? This cannot be undone — use Suppress if they may have genuinely signed up.`)) {
      return;
    }
    setBusyId(row.id);
    setMessage('');
    try {
      const resp = await fetch(
        action === 'delete'
          ? `${API_BASE}/admin/email/${row.id}`
          : `${API_BASE}/admin/email/${row.id}/${action}`,
        { method: action === 'delete' ? 'DELETE' : 'POST', headers: headers() }
      );
      if (resp.ok) {
        await load();
      } else {
        setMessage(`Failed to ${action} ${row.email}.`);
      }
    } catch (e) {
      setMessage(`Failed to ${action} ${row.email}.`);
    }
    setBusyId(null);
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  const cards = stats
    ? [
        { label: 'Active subscribers', value: stats.active, hint: 'confirmed, not unsubscribed' },
        { label: 'Pending confirm', value: stats.pending, hint: 'awaiting double opt-in' },
        { label: 'Unsubscribed', value: stats.unsubscribed, hint: '' },
        { label: 'Total ever', value: stats.total, hint: '' },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center text-videbits-700 hover:text-videbits-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to admin
        </Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-videbits-600" /> Email &amp; release digest
          </h1>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 text-sm text-videbits-600 hover:text-videbits-800"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {message && (
          <div className="mb-4 px-4 py-2 rounded-lg bg-videbits-50 text-videbits-800 text-sm">{message}</div>
        )}

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {cards.map((c) => (
                <div key={c.label} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-3xl font-bold text-gray-900">{c.value ?? 0}</div>
                  <div className="text-sm font-medium text-gray-700">{c.label}</div>
                  {c.hint && <div className="text-xs text-gray-400">{c.hint}</div>}
                </div>
              ))}
            </div>

            <DigestTemplateEditor apiBase={API_BASE} headers={headers} />

            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                <div>Schedule: <span className="font-medium text-gray-900">{stats?.cron_schedule}</span></div>
                <div>Last digest sent: <span className="font-medium text-gray-900">{fmt(stats?.last_digest_at)}</span></div>
              </div>
              <button
                onClick={sendNow}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-videbits-600 text-white rounded-lg font-medium hover:bg-videbits-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> {sending ? 'Starting…' : 'Send digest now'}
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b text-sm font-semibold text-gray-700">Recent signups</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium">Email</th>
                      <th className="px-4 py-2 font-medium">Source</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Last activity</th>
                      <th className="px-4 py-2 font-medium">Last digest</th>
                      <th className="px-4 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-400">No signups yet.</td></tr>
                    ) : (
                      recent.map((r) => (
                        <tr key={r.id || r.email} className="border-t">
                          <td className="px-4 py-2 text-gray-900">{r.email}</td>
                          <td className="px-4 py-2 text-gray-500">{r.source || '—'}</td>
                          <td className="px-4 py-2">
                            {r.unsubscribed_at ? (
                              <span className="text-gray-400">Unsubscribed</span>
                            ) : r.confirmed ? (
                              <span className="text-green-600 font-medium">Active</span>
                            ) : (
                              <span className="text-amber-600">Pending</span>
                            )}
                          </td>
                          {/* Last activity, not created_at: a dormant row can be
                              confirmed long after it was first seen. */}
                          <td className="px-4 py-2 text-gray-500">{fmt(r.last_activity_at || r.created_at)}</td>
                          <td className="px-4 py-2 text-gray-500">{fmt(r.last_digest_at)}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {r.unsubscribed_at ? (
                              <button
                                onClick={() => rowAction(r, 'resubscribe')}
                                disabled={busyId === r.id}
                                className="text-videbits-600 hover:underline disabled:opacity-40 mr-3"
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                onClick={() => rowAction(r, 'unsubscribe')}
                                disabled={busyId === r.id}
                                className="text-amber-600 hover:underline disabled:opacity-40 mr-3"
                              >
                                Suppress
                              </button>
                            )}
                            <button
                              onClick={() => rowAction(r, 'delete')}
                              disabled={busyId === r.id}
                              className="text-red-600 hover:underline disabled:opacity-40"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminEmail;
